import React, {useEffect, useState} from "react";
import {Box, Button, Card, Chip, Stack, Typography, useTheme} from "@mui/joy";
import {Ban, CheckIcon, UserIcon} from "lucide-react";
import {useUITheme} from "../../hooks/useUITheme";
import {useSubscription} from "../../hooks/useSubscription";
import {useAsync} from "../../hooks/useAsyncResult";
import {cancelSubscription} from "../../services/api/subscription";
import SensitiveActionConfirmationDialog from "../SensitiveActionConfirmationDialog";
import {useTranslation} from "react-i18next";
import {useCookies} from "react-cookie";
import NoSubscriptionComponent from "./NoSubscriptionComponent";
import {subscriptionExpiresAt} from "../../utils/subscription";

const ActiveSubscriptionComponent = () => {
    const subscription = useSubscription()
    const [cancelSubscriptionDialogVisible, setCancelSubscriptionDialogVisible] = useState(false)
    const [activeSubscription, setActiveSubscription] = useState(subscription?.subscription || {})
    const {t} = useTranslation()
    // Start date + validity days. This used today's month and year with the start day plus validity,
    // which drifted further off every month. Lifetime plans have no end date.
    const expiresAt = subscriptionExpiresAt(activeSubscription)
    const renewalDate = expiresAt ? new Date(expiresAt).toLocaleDateString() : "—"
    const cancelSubscriptionAsyncResult = useAsync(cancelSubscription, true, true, false)

    useEffect(() => {
        setActiveSubscription(subscription.subscription || {})
    }, [subscription.subscription])

    useEffect(() => {
        if (cancelSubscriptionAsyncResult.isSuccess)
            window.location.reload()
    }, [cancelSubscriptionAsyncResult.isSuccess])

    if (!subscription.hasAccess)
        return <NoSubscriptionComponent/>

    return <Stack>
        <Card data-no-glow
              sx={{padding: 2, gap: 0.5}}>

            <Chip size={"sm"} color={"primary"}>
                {t("activeSubscriptionLabel")}
            </Chip>


            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                <Stack>
                    <Typography level={"h2"}>
                        {activeSubscription.name}
                    </Typography>

                    <Typography>
                        {t("activeSubscriptionEndDateLabel", {date: renewalDate})}
                    </Typography>

                </Stack>


                <Stack gap={0.5}>
                    {!activeSubscription.isTrial && <Stack direction="row" alignItems="baseline" gap={0.5} sx={{my: 1}}>
                        <Typography level="h3">
                            {activeSubscription.localizedPrice}
                        </Typography>
                        {!activeSubscription.isLifetime && activeSubscription.frequencyLabel && (
                            <Typography level="body-sm" textColor="neutral.500">
                                /{activeSubscription.frequencyLabel}
                            </Typography>
                        )}
                    </Stack>}

                    <Button color={"danger"} onClick={() => setCancelSubscriptionDialogVisible(true)} variant={"soft"}
                            startDecorator={<Ban/>}>
                        {t("cancelSubscriptionLabel")}
                    </Button>
                </Stack>
            </Stack>

            <SensitiveActionConfirmationDialog label={t("cancelSubscriptionLabel")}
                                               onClose={() => setCancelSubscriptionDialogVisible(false)}
                                               onConfirm={cancelSubscriptionAsyncResult.execute}
                                               open={cancelSubscriptionDialogVisible}
                                               message={t("cancelSubscriptionConfirmationMessage", {subscriptionName: activeSubscription.name})}
                                               onCancel={() => setCancelSubscriptionDialogVisible(false)}
                                               negativeActionLoading={cancelSubscriptionAsyncResult.loading}/>

        </Card>

        <Typography level={"body-xs"} paddingY={0.5}>
            {t("cancelSubscriptionNotice", {renewalDate})}
        </Typography>
    </Stack>


}

export default ActiveSubscriptionComponent